import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IThongKeSiSoItem } from '../models/response/thong-ke/IThongKeSiSoItem';
import { IThongKeSiSoItemRepsone } from '../models/response/thong-ke/IThongKeSiSoItemRespone';
import { IThongKeTrangThaiItemRespone } from '../models/response/thong-ke/IThongKeTrangThaiItemRespone';
import { actions } from '../state/actions/actionsWrapper';
import { RootState } from '../state/reducers';
import { IThongKeSiSoReducer } from '../state/page-state-model/thong-ke/IThongKeSiSoReducer';
import { useCommonSelectedHook } from './useCommonSelectedHook';

const mainAction = actions.thongKe.thongKeSiSo;

let lastFilterKeyGlobal = '';

export const useThongKeSiSo = () => {
    const { dm_coso_id, nam_hoc, dm_truong_id } = useCommonSelectedHook();
    const state = useSelector((x: RootState) => x.thongKe.thongKeSiSo) as IThongKeSiSoReducer;
    const { filter, data, trangThai, expandedIds } = state;
    const dispatch = useDispatch();

    useEffect(() => {
        const stateFilterKey = `${filter.nam_hoc || ''}_${filter.dm_coso_id || 0}_${filter.dm_truong_id || 0}`;
        if (stateFilterKey && stateFilterKey !== lastFilterKeyGlobal) {
            lastFilterKeyGlobal = stateFilterKey;
        }
    }, [filter.nam_hoc, filter.dm_coso_id, filter.dm_truong_id]);

    useEffect(() => {
        const currentFilterKey = `${nam_hoc || ''}_${dm_coso_id || 0}_${dm_truong_id || 0}`;
        const stateFilterKey = `${filter.nam_hoc || ''}_${filter.dm_coso_id || 0}_${filter.dm_truong_id || 0}`;
        
        if (
            nam_hoc &&
            dm_coso_id &&
            dm_truong_id &&
            currentFilterKey !== stateFilterKey &&
            currentFilterKey !== lastFilterKeyGlobal
        ) {
            lastFilterKeyGlobal = currentFilterKey;
            const payload = {
                nam_hoc: nam_hoc,
                dm_coso_id: dm_coso_id,
                dm_truong_id: dm_truong_id
            };
            dispatch(mainAction.CHANGE_FILTER(payload));
            dispatch(mainAction.LOAD_START(payload));
            dispatch(mainAction.LOAD_TRANG_THAI_START(payload));
        }
    }, [nam_hoc, dm_coso_id, dm_truong_id, filter.nam_hoc, filter.dm_coso_id, filter.dm_truong_id, dispatch]);

    const filteredData = useMemo(() => {
        const selectedTruongId = dm_truong_id && dm_truong_id > 0 ? dm_truong_id : undefined;
        return data.filter((item: IThongKeSiSoItemRepsone) => {
            if (selectedTruongId !== undefined) {
                return item.dm_truong_id === selectedTruongId;
            }
            return item.dm_truong_id !== 8;
        });
    }, [data, dm_truong_id]);

    const filteredTrangThai = useMemo(() => {
        const selectedTruongId = dm_truong_id && dm_truong_id > 0 ? dm_truong_id : undefined;
        return trangThai.filter((item: IThongKeTrangThaiItemRespone) => {
            if (selectedTruongId !== undefined) {
                return item.dm_truong_id === selectedTruongId;
            }
            return item.dm_truong_id !== 8;
        });
    }, [trangThai, dm_truong_id]);

    const dmTrangThais = useMemo(() => {
        const map = new Map<number, IThongKeTrangThaiItemRespone>();
        filteredTrangThai.forEach((item: IThongKeTrangThaiItemRespone) => {
            if (!map.has(item.dm_trangthaihocsinh_id)) {
                map.set(item.dm_trangthaihocsinh_id, item);
            }
        });
        return map;
    }, [filteredTrangThai]);

    const dataStructed = useMemo(() => {
        const result = new Map<string, IThongKeSiSoItem>();

        const truongMap = new Map<number, {
            truong: IThongKeSiSoItemRepsone,
            khoiMap: Map<number, {
                khoi: IThongKeSiSoItemRepsone,
                heMap: Map<number, {
                    he: IThongKeSiSoItemRepsone,
                    lopMap: Map<number, IThongKeSiSoItemRepsone>
                }>
            }>
        }>();

        filteredData.forEach((item: IThongKeSiSoItemRepsone) => {
            if (!truongMap.has(item.dm_truong_id)) {
                truongMap.set(item.dm_truong_id, {
                    truong: item,
                    khoiMap: new Map()
                });
            }
            const truongData = truongMap.get(item.dm_truong_id)!;

            if (!truongData.khoiMap.has(item.dm_khoi_id)) {
                truongData.khoiMap.set(item.dm_khoi_id, {
                    khoi: item,
                    heMap: new Map()
                });
            }
            const khoiData = truongData.khoiMap.get(item.dm_khoi_id)!;

            if (!khoiData.heMap.has(item.dm_he_id)) {
                khoiData.heMap.set(item.dm_he_id, {
                    he: item,
                    lopMap: new Map()
                });
            }
            const heData = khoiData.heMap.get(item.dm_he_id)!;

            heData.lopMap.set(item.dm_lop_id, item);
        });

        truongMap.forEach((truongData, truongId) => {
            const truongItem: IThongKeSiSoItem = {
                id: truongId.toString(),
                type: "truong",
                name: truongData.truong.ten_truong,
                si_so: 0,
                items: new Map(),
                trangThaiSum: new Map()
            };

            truongData.khoiMap.forEach((khoiData, khoiId) => {
                const khoiItem: IThongKeSiSoItem = {
                    id: `${truongId}_${khoiId}`,
                    type: "khoi",
                    name: khoiData.khoi.ten_khoi,
                    si_so: 0,
                    items: new Map(),
                    trangThaiSum: new Map()
                };

                khoiData.heMap.forEach((heData, heId) => {
                    const heItem: IThongKeSiSoItem = {
                        id: `${truongId}_${khoiId}_${heId}`,
                        type: "he",
                        name: heData.he.ten_he,
                        si_so: 0,
                        items: new Map(),
                        trangThaiSum: new Map()
                    };

                    heData.lopMap.forEach((lopData, lopId) => {
                        const lopItem: IThongKeSiSoItem = {
                            id: `${truongId}_${khoiId}_${heId}_${lopId}`,
                            type: "lop",
                            name: lopData.ten_lop,
                            si_so: lopData.si_so,
                            items: new Map(),
                            trangThaiSum: new Map()
                        };

                        filteredTrangThai
                            .filter((t: IThongKeTrangThaiItemRespone) => t.dm_lop_id === lopId)
                            .forEach((t: IThongKeTrangThaiItemRespone) => {
                                const current = lopItem.trangThaiSum.get(t.dm_trangthaihocsinh_id) || 0;
                                lopItem.trangThaiSum.set(t.dm_trangthaihocsinh_id, current + t.si_so);
                            });

                        heItem.items.set(lopId.toString(), lopItem);
                        heItem.si_so += lopItem.si_so;
                    });

                    heItem.items.forEach(lop => {
                        lop.trangThaiSum.forEach((value, key) => {
                            const current = heItem.trangThaiSum.get(key) || 0;
                            heItem.trangThaiSum.set(key, current + value);
                        });
                    });

                    khoiItem.items.set(heId.toString(), heItem);
                    khoiItem.si_so += heItem.si_so;
                });

                khoiItem.items.forEach(he => {
                    he.trangThaiSum.forEach((value, key) => {
                        const current = khoiItem.trangThaiSum.get(key) || 0;
                        khoiItem.trangThaiSum.set(key, current + value);
                    });
                });

                truongItem.items.set(khoiId.toString(), khoiItem);
                truongItem.si_so += khoiItem.si_so;
            });

            truongItem.items.forEach(khoi => {
                khoi.trangThaiSum.forEach((value, key) => {
                    const current = truongItem.trangThaiSum.get(key) || 0;
                    truongItem.trangThaiSum.set(key, current + value);
                });
            });

            result.set(truongId.toString(), truongItem);
        });

        return result;
    }, [filteredData, filteredTrangThai]);

    return {
        dataStructed,
        expandedIds: expandedIds || new Map<string, string>(),
        dmTrangThais,
        rawData: filteredData,
        rawTrangThai: filteredTrangThai
    };
};

